import React from 'react';

import WorkflowArticleLayout from './components/WorkflowArticleLayout';
import WorkflowCodeBlock from './components/WorkflowCodeBlock';
import WorkflowInfoBox from './components/WorkflowInfoBox';
import WorkflowLearningPoints from './components/WorkflowLearningPoints';
import WorkflowList from './components/WorkflowList';
import WorkflowSection from './components/WorkflowSection';
import WorkflowTable from './components/WorkflowTable';
import WorkflowTableOfContents from './components/WorkflowTableOfContents';
import WorkflowText from './components/WorkflowText';
import WorkflowTipBox from './components/WorkflowTipBox';

const basicsSteps = [
  {
    label: 'Part1',
    title: 'Kiroとは: AIとの対話の基本原則',
    path: '/devdocs/workflow/kiro-basics-1',
  },
  {
    label: 'Part2',
    title: 'Steeringファイル: AIにルールを伝える方法',
    path: '/devdocs/workflow/kiro-basics-2',
  },
  {
    label: 'Part3',
    title: '機能と運用: 知っておくべきKiroの機能',
    path: '/devdocs/workflow/kiro-basics-3',
    active: true,
  },
  {
    label: 'Part4',
    title: 'ワークフロー全体像: 本プロジェクトの進め方',
    path: '/devdocs/workflow/kiro-basics-4',
  },
];

const WorkflowKiroBasics3: React.FC = () => {
  return (
    <WorkflowArticleLayout
      title="Kiro基本編 Part3 — 機能と運用: 知っておくべきKiroの機能"
      seriesName="Kiro基本編"
      tags={['Autopilot', 'Specs', 'Hooks', 'Hallucination']}
      seriesSteps={basicsSteps}
      prevLink={{ label: 'Part2: Steeringファイル', path: '/devdocs/workflow/kiro-basics-2' }}
      nextLink={{ label: 'Part4: ワークフロー全体像', path: '/devdocs/workflow/kiro-basics-4' }}
    >
      {/* この記事で学べること */}
      <WorkflowLearningPoints
        items={[
          'Autopilot / Supervised モードの使い分け',
          'Specs（仕様策定モード）の概要とVibeとの違い',
          'Hooks（Agent Hooks）の仕組みと利用場面',
          'AIの「幻覚」への対処法',
        ]}
      />

      <WorkflowTableOfContents
        items={[
          { id: 'section-1', label: 'Autopilot / Supervised モードの使い分け' },
          { id: 'section-2', label: 'Specs（仕様策定モード）' },
          { id: 'section-3', label: 'Hooks（Agent Hooks）' },
          { id: 'section-4', label: 'AIの「幻覚」への対処法' },
        ]}
      />

      {/* 1. Autopilot / Supervised */}
      <WorkflowSection title="Autopilot / Supervised モードの使い分け" id="section-1" number={1}>
        <WorkflowText>
          Kiroには2つの自律モードがあり、AIがどこまで自動で作業するかを制御できます。
        </WorkflowText>

        <WorkflowTable
          headers={['モード', '挙動', 'ユーザーの関わり']}
          rows={[
            [
              'Autopilot（デフォルト）',
              'AIがタスクを最後まで自律的に実行する。ファイル作成・編集・コマンド実行を自動で行う',
              '完了後にまとめて変更をレビュー。必要なら revert',
            ],
            [
              'Supervised',
              'ファイル編集のたびに停止し、変更をハンク（差分の塊）単位で提示する',
              '各ハンクごとに Accept / Reject を選択。細かく制御できる',
            ],
          ]}
        />

        <WorkflowSection title="いつどちらを使うか" subNumber="1.1">
          <WorkflowTable
            headers={['シーン', '推奨モード', '理由']}
            rows={[
              ['ワークフローに慣れた後の通常開発', 'Autopilot', '停止が少なく作業速度が出る'],
              [
                'Steeringファイルの初期整備',
                'Supervised',
                '1ファイルずつ確認しないと意図しないルールが書かれる',
              ],
              [
                '影響範囲が不明な既存コード修正',
                'Supervised',
                '予期しないファイルへの変更を事前にキャッチできる',
              ],
              [
                '新規機能の一括生成（DD書→コード）',
                'Autopilot',
                '大量ファイル生成を逐一承認するのは非効率',
              ],
              [
                'セキュリティ関連の設定変更',
                'Supervised',
                '認証・認可・環境設定の変更は1つずつ確認する',
              ],
            ]}
          />
        </WorkflowSection>

        <WorkflowInfoBox>
          本プロジェクトでは基本的にAutopilotで運用し、api_project_rule の停止ポイント（0: /
          1:）でレビューを挟んでいます。Steeringファイルの進行ルール自体がレビューゲートの役割を果たすため、Supervised
          にする場面はほぼありませんでした。
        </WorkflowInfoBox>
      </WorkflowSection>

      {/* 2. Specs */}
      <WorkflowSection title="Specs（仕様策定モード）" id="section-2" number={2}>
        <WorkflowInfoBox>
          本プロジェクトではSpecsモードを利用していません。代わりにSteeringファイル（api_project_rule.md
          等）でワークフローを制御しています。Specsは「Kiro標準の仕様策定プロセスを使いたい場合」に有効です。
        </WorkflowInfoBox>

        <WorkflowSection title="Vibe と Specs の違い" subNumber="2.1">
          <WorkflowTable
            headers={['観点', 'Vibe', 'Specs']}
            rows={[
              ['形式', '自由な対話形式', '要件→設計→タスクの3段階プロセス'],
              [
                '成果物管理',
                'チャット内に残る（ファイル化は手動）',
                '.kiro/specs/ に構造化されたファイルとして自動保存',
              ],
              ['進行管理', 'ユーザーが自分で管理', 'タスクリストとして進行状況が可視化される'],
              ['向いている作業', '探索的な議論、小さな修正、質問', '新機能開発、複雑な変更の計画'],
              ['カスタマイズ性', 'Steeringで自由に制御可能', 'Kiro標準のプロセスに沿う'],
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="Specs の3段階プロセス" subNumber="2.2">
          <WorkflowTable
            headers={['段階', '内容', '出力']}
            rows={[
              ['Requirements', '何を作るかの要件を整理する', '要件リスト（箇条書き）'],
              ['Design', '技術的な設計を行う', '設計ドキュメント'],
              ['Tasks', '実装タスクに分解する', 'チェックリスト形式のタスクリスト'],
            ]}
          />
        </WorkflowSection>

        <WorkflowTipBox title="なぜSpecsではなくSteeringベースのワークフローを選んだのか">
          <WorkflowList
            items={[
              '成果物の形式を完全に制御したい（DD書のフォーマット、API仕様書の構造など、プロジェクト固有の形式がある）',
              'ウォーターフォール的な工程管理が必要（設計書Rv指摘、用語集整合、権限一覧連動など、工程間の連動ルールが複雑）',
              '停止ポイントの制御が必要（0: / 1: プロトコルによるユーザー確認タイミングを細かく制御したい）',
            ]}
          />
          <WorkflowText sx={{ mt: 1 }}>
            どちらが優れているかではなく、プロジェクトの要件に合わせて選択してください。
          </WorkflowText>
        </WorkflowTipBox>
      </WorkflowSection>

      {/* 3. Hooks */}
      <WorkflowSection title="Hooks（Agent Hooks）" id="section-3" number={3}>
        <WorkflowInfoBox>
          本プロジェクトではHooksを利用していません。api_project_rule
          のワークフロー自体が「成果物作成→停止→確認」のサイクルで制御されているため、Hook
          による自動化の必要がありませんでした。ただし、知識として知っておくと自動化の選択肢が広がります。
        </WorkflowInfoBox>

        <WorkflowSection title="Hooks でできること" subNumber="3.1">
          <WorkflowTable
            headers={['トリガー', '発火タイミング', '利用例']}
            rows={[
              ['PostFileSave', 'ファイル保存時', '保存時に自動リント、フォーマッター実行'],
              [
                'PreToolUse',
                'AIがツールを実行する直前',
                '書き込み操作にコーディング規約チェックを挟む',
              ],
              ['PostToolUse', 'AIがツールを実行した直後', 'ファイル変更後にテスト自動実行'],
              ['SessionStart', 'セッション開始時', '最新のGit状況やTODOリストをコンテキストに注入'],
              ['PostTaskExec', 'Specタスク完了後', 'タスク完了ごとにテストスイート実行'],
              [
                'UserPromptSubmit',
                'ユーザーがメッセージ送信時',
                '入力内容のバリデーションや前処理',
              ],
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="Hook の基本構造" subNumber="3.2">
          <WorkflowCodeBlock
            language="json"
            code={`{
  "version": "v1",
  "hooks": [{
    "name": "Lint on Save",
    "trigger": "PostFileSave",
    "matcher": "\\\\.(ts|tsx)$",
    "action": { "type": "command", "command": "npm run lint" }
  }]
}`}
          />
          <WorkflowTable
            headers={['フィールド', '説明']}
            rows={[
              ['trigger', '発火イベント（PascalCase）'],
              ['matcher', '正規表現でフィルタ（対象ツール名やファイルパス）'],
              ['action.type', 'command（シェルコマンド実行）または agent（プロンプト注入）'],
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="exit code の意味（command タイプ）" subNumber="3.3">
          <WorkflowTable
            headers={['exit code', '意味']}
            rows={[
              ['0', '成功。stdoutが後続処理に渡される'],
              ['2', 'ブロック。AIのアクションを中止する（PreToolUse等）'],
              ['その他', 'サイレント失敗。ブロックしない'],
            ]}
          />
        </WorkflowSection>

        <WorkflowTipBox title="Hooksを使うべきケース">
          <WorkflowList
            items={[
              '静的解析の自動実行: ファイル保存のたびにリンターを走らせ、エラーがあればAIに修正させる',
              'アクセス制御: 特定ファイル（本番設定等）への書き込みを PreToolUse で検知し、ユーザーに確認を求める',
              'テストの自動実行: Specタスク完了後に自動でテストスイートを回し、失敗があればAIに修正させる',
              'コンテキスト注入: SessionStart で「最新のissue一覧」や「今日のTODO」をコンテキストに注入する',
            ]}
          />
          <WorkflowText sx={{ mt: 1 }}>
            逆に、本プロジェクトのようにSteeringファイルのワークフロー自体に停止ポイントが組み込まれている場合は、Hooksによる追加制御はオーバーヘッドになることが多いです。
          </WorkflowText>
        </WorkflowTipBox>
      </WorkflowSection>

      {/* 4. AIの「幻覚」への対処法 */}
      <WorkflowSection title="AIの「幻覚」への対処法" id="section-4" number={4}>
        <WorkflowText>
          AIは「もっともらしいが事実と異なる出力」を生成することがあります。これを幻覚（Hallucination）と呼びます。コード生成においては、存在しないAPIやメソッドの利用、仕様にない処理の追加、誤った型の使用などの形で現れます。
        </WorkflowText>

        <WorkflowSection title="コード生成で起きやすい幻覚のパターン" subNumber="4.1">
          <WorkflowTable
            headers={['パターン', '具体例', '原因']}
            rows={[
              [
                '存在しないメソッドの呼び出し',
                'repository.findByNameAndStatus() — 実際には定義していない',
                '一般的なSpring Dataパターンからの推測',
              ],
              [
                '仕様にないフィールドの追加',
                'ResponseDtoに createdBy を勝手に追加',
                '「あった方がよさそう」という推測',
              ],
              [
                '誤ったライブラリAPIの使用',
                'MyBatisの @Results を誤った構文で記述',
                '学習データ内の別バージョンの構文と混同',
              ],
              [
                'テーブルに存在しないカラムの参照',
                'SQLで deleted_at を参照 — 実際は is_deleted',
                '一般的な論理削除パターンからの推測',
              ],
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="幻覚を防ぐための対策" subNumber="4.2">
          <WorkflowTable
            headers={['対策', '説明', '本PJでの実装']}
            rows={[
              [
                'Steeringで制約を明示する',
                '許可する型・命名規則・使用ライブラリを明記する',
                'tech.md でコーディング規約を網羅的に定義',
              ],
              [
                '既存コードのパターン参照を指示する',
                '「既存の○○クラスと同じパターンで作って」と指示する',
                'api_project_rule のクレジット節約ルール',
              ],
              [
                'スキーマ定義を入力に含める',
                'SQLを生成する際は必ずテーブル定義をコンテキストに入れる',
                'mk_instructions 作成時に関連スキーマ定義を確認',
              ],
              [
                'DD書で処理を詳細に記述する',
                'AIの推測余地を減らす',
                'create_dd.md で「誰が見てもコードが書けるレベル」と明記',
              ],
              [
                '設計書ベースでテストを作る',
                '実装の幻覚を独立した視点で検出する',
                'Phase3 で「実装ベースではなく設計書ベース」と明記',
              ],
              [
                'コンパイル・ビルドで検証する',
                '存在しないメソッドの呼び出しはコンパイルエラーで検出',
                'Phase2 完了前にビルドを通すルール',
              ],
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="幻覚に気づいたときの対処" subNumber="4.3">
          <WorkflowTable
            headers={['状況', '対処']}
            rows={[
              [
                'レビュー中に気づいた',
                '1: で指摘し、正しい仕様（スキーマ定義やDD書）を参照するよう指示する',
              ],
              ['テストで検出された', 'バグとして修正。原因がDD書の曖昧さなら遡及修正する'],
              [
                '繰り返し同じ幻覚が起きる',
                'Steeringルールに「〇〇は使用しない」「〇〇はこのパターンで実装する」と追記する',
              ],
            ]}
          />
        </WorkflowSection>

        <WorkflowInfoBox>
          幻覚を完全にゼロにすることはできません。大事なのは「幻覚が混入しても後工程で検出・修正できる仕組み」を持つことです。本プロジェクトではDD書の詳細記述（Phase1）→コンパイル検証（Phase2）→設計書ベースのテスト（Phase3）という多層防御で対処しています。
        </WorkflowInfoBox>
      </WorkflowSection>
    </WorkflowArticleLayout>
  );
};

export default WorkflowKiroBasics3;
